import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import {
    APIAppTemplateModel,
    getInputSchema,
    getParameterSchema
} from "../../../../../../../Models/API/AppTemplateModel"
import {
    Loader,
    Title,
    Text,
    OnboardingScreenContainer,
    Checkbox
} from "dash-styled"
import { LinkButton } from "../../../../../../Common/LinkButton/LinkButton"
import { OnboardingProgress } from "../../../../Common/OnboardingProgress/OnboardingProgress"
import { TemplateInstantiateStage } from "../../../../../../../Models/Client/TemplateInstantiation"
import { useDispatch } from "react-redux"
import { resetOnboarding } from "../../../../../../../Actions/API/AppTemplate/Interface/TemplateInstantiateActions"
import { useSampleAppData } from "../../../../../../../Hooks/useSampleAppData"
import { InputFormRender } from "../../../../../../Common/InputFormRender/InputFormRender"
import type { AjvError } from "@rjsf/core"
import { changeInputValue } from "../../../../../../../Actions/Client/InputRender/InputRenderActions"
import { useAppState } from "../../../../../../../Hooks/useReduxState"
import type { SchemaFormRefProps } from "resha"
import { useBoolean } from "../../../../../../../Hooks/useBoolean"
import { DocumentExtractionParameters } from "./DocumentsExtraction"
import { DocumentsExtraction } from "../../../../../../Common/SchemaForm/DocumentsExtraction"

interface SetParametersStageProps {
    template: APIAppTemplateModel
    formID: string
    inputData: unknown
    demo?: {
        active: boolean
        toggle: () => void
    }
    onBack?: () => void
    onForward?: () => void
}

export const SetParametersStage = ({
    template,
    formID,
    inputData,
    onBack,
    onForward,
    demo
}: SetParametersStageProps) => {
    const dispatch = useDispatch()
    const [creating, setCreating] = useState<boolean>(false)
    const [errors, setErrors] = useState<AjvError[]>([])
    const formData = useAppState(
        state => state.client.inputRender.entries[formID]
    )
    const inputSchema = useMemo(
        () => getInputSchema(template, true),
        [template]
    )
    const parameterSchema = useMemo(
        () => getParameterSchema(template, true),
        [template]
    )

    const formDataRef = useRef(formData)

    const [liveValidateForm, startLiveValidatingForm, stopLiveValidatingForm] =
        useBoolean(false)
    const formRef = useRef<SchemaFormRefProps>(null)
    // this is required when landing here from labelling ui
    useEffect(() => {
        dispatch(resetOnboarding())
    }, [dispatch])

    const loading = useSampleAppData(template)

    const handleError = useCallback(
        (errors: AjvError[]) => {
            dispatch(
                changeInputValue(formID, {
                    values: formDataRef.current?.values,
                    surviveReroute: true,
                    errors: [],
                    formErrors: errors
                })
            )
            startLiveValidatingForm()
        },
        [dispatch, startLiveValidatingForm, formID]
    )

    const handleExtraError = useCallback((errors: AjvError[]) => {
        setErrors(errors)
    }, [])

    const handleParameterChange = useCallback(
        (values: unknown) => {
            dispatch(
                changeInputValue(formID, {
                    values,
                    surviveReroute: true,
                    formErrors: [],
                    errors: []
                })
            )
        },
        [formID, dispatch]
    )

    const handleParameterSubmit = useCallback(
        async (data: unknown) => {
            try {
                setCreating(true)
                handleParameterChange(data)
                stopLiveValidatingForm()
                await onForward?.()
            } finally {
                setCreating(false)
            }
        },
        [onForward, stopLiveValidatingForm, handleParameterChange]
    )

    const handleForward = useCallback(async () => {
        if (parameterSchema !== undefined) {
            formRef.current?.submit()
        } else {
            try {
                if (onForward !== undefined) {
                    setCreating(true)
                    await onForward()
                }
            } finally {
                setCreating(false)
            }
        }
    }, [onForward, parameterSchema])

    useEffect(() => {
        formDataRef.current = formData
    }, [formData])

    useEffect(() => () => stopLiveValidatingForm(), [stopLiveValidatingForm])

    if (loading) {
        return (
            <OnboardingScreenContainer forward={false} onForward={onForward}>
                <OnboardingProgress
                    currentStage={TemplateInstantiateStage.setParameters}
                    back={onBack !== undefined}
                    onBack={onBack}
                />
                <Loader />
            </OnboardingScreenContainer>
        )
    }

    const isDocumentExtraction =
        parameterSchema?.uiSchema["ui:field"] ===
        "https://widgets.super.ai/document-extraction-parameter@1"

    const pBlock =
        parameterSchema !== undefined ? (
            <InputFormRender
                shouldRenderFieldInPanel
                testId="onboarding-parameters-form"
                ref={formRef}
                {...parameterSchema}
                formData={formData?.values ?? template.defaultAppParams?.params}
                onChange={handleParameterChange}
                liveValidate={liveValidateForm}
                onError={handleError}
                onExtraError={
                    isDocumentExtraction ? handleExtraError : undefined
                }
                onSubmit={handleParameterSubmit}
            />
        ) : (
            <Text>
                Data program has no parameters, please proceed to the next step.
            </Text>
        )

    return parameterSchema?.uiSchema["ui:field"] ===
        "https://widgets.super.ai/document-extraction-parameter@1" ? (
        <DocumentsExtraction
            demo={demo}
            submitting={creating}
            onForward={
                creating || errors.length > 0 ? undefined : handleForward
            }
            onBack={onBack}
            input={{
                formData: inputData,
                ...inputSchema
            }}
            parameterSchema={parameterSchema}
            output={{
                ref: formRef,
                formData: formData?.values ?? template.defaultAppParams?.params,
                onChange: handleParameterChange,
                liveValidate: liveValidateForm,
                onError: handleError,
                onExtraError: handleExtraError,
                onSubmit: handleParameterSubmit
            }}
            children={DocumentExtractionParameters}
        />
    ) : (
        <OnboardingScreenContainer
            celebrate={false}
            forward={true}
            onForward={
                creating || (isDocumentExtraction && errors.length > 0)
                    ? undefined
                    : handleForward
            }
        >
            <OnboardingProgress
                currentStage={TemplateInstantiateStage.setParameters}
                back={onBack !== undefined}
                onBack={onBack}
            />
            {creating ? (
                <Loader />
            ) : (
                <div id="maincontent" className="flex w-full pb-4">
                    <div className="w-full md:w-1/4 px-4 mt-14">
                        <div
                            data-testid="input-data"
                            className="border-gray-300 border-solid border rounded-lg px-4 py-2"
                        >
                            <div className="flex w-full justify-between py-4">
                                <Title
                                    component="h3"
                                    variant="xl"
                                    noMargin={true}
                                    weight="semibold"
                                    color="darkviolet"
                                >
                                    Input data
                                </Title>
                                {onBack && (
                                    <LinkButton
                                        testId="set-params-back"
                                        className="text-xs"
                                        onClick={onBack}
                                    >
                                        Change
                                    </LinkButton>
                                )}
                            </div>
                            <InputFormRender
                                formData={inputData}
                                {...inputSchema}
                                skipMissingDataField={true}
                            />
                        </div>
                    </div>
                    <div className="flex flex-col px-4 w-full md:w-3/4 lg:w-3/4 xl:w-3/4">
                        {pBlock}
                        {demo && (
                            <div className="flex items-center justify-center">
                                <Checkbox
                                    variant="modern"
                                    className="text-rhino-900"
                                    label="I will process myself (demo mode)"
                                    onChange={demo.toggle}
                                    isChecked={demo.active}
                                    testId="demo-mode-checkbox"
                                />
                            </div>
                        )}
                    </div>
                </div>
            )}
        </OnboardingScreenContainer>
    )
}
